import React, { FC, useState, useMemo } from "react";
import { AccruedFee, Phase, CumulativeAccruedFee, CompletionLog } from "../../data-types";
import { message, Slider, Typography, Checkbox, DatePicker, Form } from "antd";
import type { DatePickerProps } from "antd";
import styles from "./matter-details-modal.css";
import { EvaChart } from "../earned-value-analysis";
import moment from "moment";

const {
  ["tab-content"]: tabContentClassName,
  ["tab-content-columns"]: tabContentColumnsClassName,
  ["slider"]: sliderClassName,
  ["phase-and-tasks-container"]: phaseAndTasksContainerClassName,
  ["phase-container"]: phaseContainerClassName,
  ["phase-title-container"]: phaseTitleContainerClassName,
  ["phase-title"]: phaseTitleClassName,
  ["phase-text"]: phaseTextClassName,
  ["checkbox-container"]: checkboxContainerClassName,
  ["left-column"]: leftColumnClassName,
  ["right-column"]: rightColumnClassName,
  ["chart"]: chartClassName,
} = styles as any;

export type ProgressTabProps = {
  completionPercentage: number;
  phases: Phase[];
  onTogglePhaseComplete: (phaseId: number, completed: boolean) => void;
  onCompletionPercentageChange: (percentage: number) => void;
  onPhaseCompletionPercentageChange: (phaseId: number, percentage: number) => void;
  cumulativeAccruedFees: CumulativeAccruedFee[];
  accruedFee: AccruedFee;
  completionLogs: CompletionLog[];
  currencySymbol: string;
  startDate?: Date;
  deadline?: Date;
  onDeadlineChange: (deadline: Date) => void;
};

const formatter = (value: number = 0) => `${value}%`;

export const ProgressTab: FC<ProgressTabProps> = ({
  completionPercentage,
  phases,
  onTogglePhaseComplete,
  onCompletionPercentageChange,
  onPhaseCompletionPercentageChange,
  cumulativeAccruedFees,
  accruedFee,
  completionLogs,
  currencySymbol,
  startDate,
  deadline,
  onDeadlineChange,
}) => {
  const showContent = accruedFee.accruedTimes.length > 0 && cumulativeAccruedFees.length > 0;
  const [stagedCompletionPercentage, setStagedCompletionPercentage] =
    useState<number>(completionPercentage);
  const [stagedPhaseCompletionPercentages, setStagedPhaseCompletionPercentages] = useState<{
    [phaseId: number]: number;
  }>(
    phases.reduce(
      (acc, phase) => ({
        ...acc,
        [phase.id]: phase.markedComplete ? 100 : phase.completionPercentage ?? 0,
      }),
      {}
    )
  );

  const trueStartDate = useMemo(() => {
    const completionLogDates = completionLogs.map((log) => new Date(log.createdAt));
    const accruedFeeDates = cumulativeAccruedFees.map((fee) => new Date(fee.date));
    const allDates = [startDate, ...completionLogDates, ...accruedFeeDates].filter(Boolean);
    return new Date(
      Math.min.apply(
        null,
        allDates.map((date) => date!.getTime())
      )
    );
  }, [startDate, completionLogs, cumulativeAccruedFees]);

  const sortedPhases = [...phases]
    .map((phase) => ({
      ...phase,
      tasks: phase.tasks ? [...phase.tasks].sort((a, b) => a.order - b.order) : [],
    }))
    .sort((a, b) => a.order - b.order);

  const calculateCompletionPercentage = (update?: { phaseId: number; newValue: number }) => {
    const updatedCompletionPercentages = { ...stagedPhaseCompletionPercentages };
    if (update) {
      updatedCompletionPercentages[update.phaseId] = update.newValue;
    }
    const averageCompletionPercentage =
      Object.values(updatedCompletionPercentages).reduce((acc, curr) => acc + curr, 0) /
      (sortedPhases.length || 1);
    return Math.round(averageCompletionPercentage);
  };

  // useEffect(() => {
  //   if (phaseOverride) {
  //     const completionPercentage = calculateCompletionPercentage();
  //     setStagedCompletionPercentage(completionPercentage);
  //   }
  // }, [stagedPhaseCompletionPercentages]);

  const updateStagedCompletionPercentage = (update?: { phaseId: number; newValue: number }) => {
    const completionPercentage = calculateCompletionPercentage(update);
    setStagedCompletionPercentage(completionPercentage);
  };

  const updateDeadline: DatePickerProps["onChange"] = (date) => {
    if (date) {
      if (date.toDate() > trueStartDate) {
        onDeadlineChange(date.toDate());
      } else {
        message.error({
          content: "Deadline must be after the start date",
          duration: 5,
        });
      }
    }
  };

  return (
    <div className={tabContentClassName}>
      <div className={tabContentColumnsClassName}>
        <div className={leftColumnClassName}>
          <div>
            <Typography.Title level={4}>
              Matter Completion: {stagedCompletionPercentage}%
            </Typography.Title>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
              <Form.Item label="Deadline" style={{ marginBottom: 0 }}>
                <DatePicker
                  value={deadline ? moment(deadline) : undefined}
                  onChange={updateDeadline}
                  status={deadline && deadline < new Date() ? "warning" : undefined}
                />
              </Form.Item>
              {deadline && deadline < new Date() && (
                <Typography.Text style={{ marginLeft: "15px" }} type="danger">
                  Warning: deadline has passed
                </Typography.Text>
              )}
              {!deadline && (
                <Typography.Text style={{ marginLeft: "15px" }} type="secondary">
                  Set a matter deadline for more progress insights
                </Typography.Text>
              )}
            </div>
          </div>
          {showContent ? (
            <>
              <div className={sliderClassName}>
                <Typography.Text>
                  Adjust completion percentage manually using the slider below:
                </Typography.Text>
                <Slider
                  value={stagedCompletionPercentage}
                  tooltip={{ formatter }}
                  onChange={(value) => {
                    setStagedCompletionPercentage(value);
                  }}
                  onAfterChange={onCompletionPercentageChange}
                />
              </div>
              {phases.length > 0 && (
                <>
                  <Typography.Text>Or adjust progress at Phase level:</Typography.Text>
                  <div className={phaseAndTasksContainerClassName}>
                    {sortedPhases.map((phase) => (
                      <div
                        key={phase.id}
                        className={phaseContainerClassName}
                        style={{ display: "flex", marginBottom: "20px" }}
                      >
                        <div className={phaseTitleContainerClassName} style={{ flex: 1 }}>
                          <div className={phaseTitleClassName}>
                            <span className={phaseTextClassName}>{phase.title}</span>
                          </div>
                          <div className={sliderClassName}>
                            <Slider
                              value={stagedPhaseCompletionPercentages[phase.id]}
                              onChange={(value) => {
                                setStagedPhaseCompletionPercentages({
                                  ...stagedPhaseCompletionPercentages,
                                  [phase.id]: value,
                                });
                              }}
                              onAfterChange={(value) => {
                                if (value !== 100) {
                                  onTogglePhaseComplete(phase.id, false);
                                }
                                onPhaseCompletionPercentageChange(phase.id, value);
                                updateStagedCompletionPercentage({
                                  phaseId: phase.id,
                                  newValue: value,
                                });
                                onCompletionPercentageChange(
                                  calculateCompletionPercentage({
                                    phaseId: phase.id,
                                    newValue: value,
                                  })
                                );
                              }}
                            />
                            <span>{stagedPhaseCompletionPercentages[phase.id]}%</span>
                          </div>
                        </div>
                        <div className={checkboxContainerClassName} style={{ flexShrink: 0 }}>
                          <Checkbox
                            checked={phase.markedComplete}
                            onChange={(e) => {
                              onTogglePhaseComplete(phase.id, e.target.checked);
                              if (e.target.checked) {
                                setStagedPhaseCompletionPercentages({
                                  ...stagedPhaseCompletionPercentages,
                                  [phase.id]: 100,
                                });
                                updateStagedCompletionPercentage({
                                  phaseId: phase.id,
                                  newValue: 100,
                                });
                              } else {
                                setStagedPhaseCompletionPercentages({
                                  ...stagedPhaseCompletionPercentages,
                                  [phase.id]: phase.completionPercentage ?? 0,
                                });
                                updateStagedCompletionPercentage({
                                  phaseId: phase.id,
                                  newValue: phase.completionPercentage ?? 0,
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <Typography.Text style={{ color: "red" }}>
              More insights will appear here when time has been recorded against this matter.
            </Typography.Text>
          )}
        </div>
        <div className={rightColumnClassName}>
          {showContent && (
            <div className={chartClassName}>
              <EvaChart
                title="Matter Progress"
                data={cumulativeAccruedFees.map((caf) => ({
                  x: caf.date,
                  y: caf.amount,
                }))}
                completionLogs={completionLogs.map((log) => ({
                  x: log.createdAt,
                  y: log.completionPercentage,
                }))}
                completionPercentage={stagedCompletionPercentage}
                plannedFee={accruedFee.plannedFee}
                startDate={trueStartDate}
                deadline={deadline}
                currencySymbol={currencySymbol}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
